import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ShoppingBag, Check, ArrowLeft } from "lucide-react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import ReviewSection from "@/components/ReviewSection";
import PaystackCheckout from "@/components/PaystackCheckout";
import CurrencySelector from "@/components/CurrencySelector";
import { useCurrency } from "@/contexts/CurrencyContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProductDetail = () => {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(true);
  const { formatPrice, symbol, currency } = useCurrency();

  useEffect(() => {
    fetchProduct();
  }, [id]);

  const fetchProduct = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/products/${id}`);
      setProduct(response.data);
    } catch (error) {
      console.error("Error fetching product:", error);
      toast.error("Failed to load product");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-slate-900 mb-4">Product not found</h2>
          <Link to="/products">
            <Button>Back to Products</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* 95% OFF Banner */}
      <div className="bg-gradient-to-r from-red-600 via-red-500 to-orange-500 text-white py-2 px-4">
        <p className="text-center text-sm sm:text-base font-bold animate-pulse">
          🔥 MASSIVE SALE: 95% OFF ALL FORMULAS! Limited Time Only! 🔥
        </p>
      </div>

      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-md bg-white/80 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <Link to="/" className="flex items-center gap-2">
              <ShoppingBag className="h-8 w-8 text-slate-900" />
              <span className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Formula Library</span>
            </Link>
            <CurrencySelector />
          </div>
        </div>
      </header>

      {/* Back Button */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Link to="/products" className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors" data-testid="back-to-products">
          <ArrowLeft className="h-4 w-4" />
          Back to Products
        </Link>
      </div>

      {/* Product Detail */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Product Image */}
          <div>
            <div className="aspect-square rounded-2xl overflow-hidden border border-slate-200">
              <img 
                src={product.image_url} 
                alt={product.title}
                className="w-full h-full object-cover"
                data-testid="product-image"
              />
            </div>
          </div>

          {/* Product Info */}
          <div>
            <div className="text-sm text-sky-600 font-medium mb-2">{product.category}</div>
            <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6" style={{ fontFamily: 'Space Grotesk, sans-serif' }} data-testid="product-title">
              {product.title}
            </h1>
            <p className="text-lg text-slate-600 mb-8" style={{ lineHeight: '1.6' }} data-testid="product-description">
              {product.description}
            </p>

            {/* Features */}
            {product.features && product.features.length > 0 && (
              <div className="mb-8">
                <h3 className="text-lg font-bold text-slate-900 mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  What's Included:
                </h3>
                <ul className="space-y-3" data-testid="product-features">
                  {product.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <div className="bg-sky-100 rounded-full p-1 mt-1">
                        <Check className="h-4 w-4 text-sky-600" />
                      </div>
                      <span className="text-slate-700">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Price */}
            <div className="bg-slate-50 border border-slate-200 rounded-2xl p-8 mb-8">
              {/* Discount Badge */}
              {product.discount_percentage > 0 && (
                <div className="flex justify-center mb-4">
                  <span className="bg-red-500 text-white text-sm font-bold px-4 py-2 rounded-full">
                    🔥 {product.discount_percentage}% OFF - LIMITED TIME!
                  </span>
                </div>
              )}
              
              <div className="flex items-center justify-between mb-2">
                <span className="text-slate-600">Price</span>
                <div className="text-right">
                  {product.original_price && product.original_price !== product.price && (
                    <span className="text-lg text-slate-400 line-through mr-3" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                      {formatPrice(product.original_price)}
                    </span>
                  )}
                  <span className="text-4xl font-bold text-red-600" style={{ fontFamily: 'JetBrains Mono, monospace' }} data-testid="product-price">
                    {formatPrice(product.price)}
                  </span>
                </div>
              </div>
              <p className="text-right text-sm text-slate-500 mb-6">
                {currency !== 'USD' && `(≈ $${product.price} USD)`}
                {product.original_price && product.original_price !== product.price && (
                  <span className="text-green-600 font-medium ml-2">
                    You save {formatPrice(product.original_price - product.price)}!
                  </span>
                )}
              </p>

              {/* Checkout Form */}
              <div className="space-y-4">
                <div>
                  <Label htmlFor="email" className="text-slate-700 mb-2 block">
                    Email Address
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="h-12 rounded-lg border-slate-200"
                    data-testid="email-input"
                  />
                  <p className="text-sm text-slate-500 mt-2">
                    We'll send your download link to this email
                  </p>
                </div>

                {/* Paystack Payment */}
                {email && (
                  <PaystackCheckout
                    amount={product.price}
                    email={email}
                    productId={id}
                    onError={(error) => {
                      toast.error('Payment failed. Please try again.');
                    }}
                  />
                )}

                {!email && (
                  <p className="text-center text-amber-600 text-sm">
                    Please enter your email address above to continue
                  </p>
                )}
              </div>
            </div>

            {/* Support Info */}
            <div className="bg-sky-50 border border-sky-100 rounded-2xl p-6">
              <h4 className="font-bold text-slate-900 mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                24/7 Support Available
              </h4>
              <p className="text-sm text-slate-600">
                Need help with your formulations? Contact us on WhatsApp at <strong>+27 730 758 123</strong>
              </p>
            </div>
          </div>
        </div>

        {/* Reviews Section */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
          <ReviewSection productId={id} />
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-slate-400">
            © 2026 Formula Library. All rights reserved.
          </p>
          <div className="flex justify-center gap-6 mt-4">
            <Link to="/terms" className="text-slate-400 hover:text-white transition-colors">Terms & Conditions</Link>
            <Link to="/refund-policy" className="text-slate-400 hover:text-white transition-colors">Refund Policy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ProductDetail;
